import { useCallbackRef } from '@chakra-ui/react';
import { Client } from '@twilio/conversations';
import { useEffect, useState } from 'react';

interface ITwilioConnectionResponse {
  client: Client;
  error: Error;
}

interface ITwilioConnectionOptions {
  onTokenAboutToExpire: () => void;
}

export const useTwilioConnection = (token: string, options: ITwilioConnectionOptions): ITwilioConnectionResponse => {
  const [client, setClient] = useState<Client>(undefined as Client);
  const [error, setError] = useState<Error>();
  const [isInitialized, setIsInitialized] = useState(false);

  const onTokenAboutToExpire = useCallbackRef(options.onTokenAboutToExpire);

  useEffect(() => {
    if (token) {
      if (client) {
        client.updateToken(token);
      } else {
        const twilioClient = new Client(token);

        setClient(twilioClient);
      }
    }
  }, [token, client]);

  useEffect(() => {
    if (!client) {
      return;
    }
    const tokenRevalidationHandler = async () => {
      onTokenAboutToExpire();
    };

    const connectionErrorHandler = async (err) => {
      if (err.terminal) {
        setError(new Error(err.message, { cause: err.errorCode }));
      }
    };

    const stateChangeHandler = async (state) => {
      setIsInitialized(state === 'initialized');
    };

    client?.on('tokenAboutToExpire', tokenRevalidationHandler);
    client?.on('stateChanged', stateChangeHandler);
    client?.on('connectionError', connectionErrorHandler);

    return () => {
      client?.off('tokenAboutToExpire', tokenRevalidationHandler);
      client?.off('stateChanged', stateChangeHandler);
      client?.off('connectionError', connectionErrorHandler);
    };
  }, [client, onTokenAboutToExpire]);

  return {
    client: isInitialized ? client : undefined,
    error,
  };
};
